import classnames from 'classnames';
import { useId, type FC, type MouseEvent } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';

import { Badge } from 'dibs-elements/exports/Badge.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';

import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import { AuctionPricePaddle } from 'dibs-auctions/exports/AuctionPricePaddle.tsx';
import { SRC_SET_SIZES_RESP_CAROUSEL_TILE } from '../../../constants/imageConstants.js';
import { type FavoritesProviderChildrenProps } from '../../../components/global/FavoriteIcons/FavoritesProviderChildrenProps.ts';
import { FavoritesItem } from '../../../components/global/FavoriteIcons/FavoritesItem.tsx';

// styles
import styles from './PdpRespSimilarItemCarousel.scss';
import { type PdpRespSimilarItemCarousel_item$key } from './__generated__/PdpRespSimilarItemCarousel_item.graphql.ts';
import { type PdpRespSimilarItemCarousel_baseItem$key } from './__generated__/PdpRespSimilarItemCarousel_baseItem.graphql.ts';

type PdpRespSimilarItemCarouselProps = {
    index: number;
    item: PdpRespSimilarItemCarousel_item$key | null | undefined;
    baseItem: PdpRespSimilarItemCarousel_baseItem$key | null | undefined;
    isCondensed?: boolean;
    onClick: (index: number, event: MouseEvent | null) => void;
    favorites?: FavoritesProviderChildrenProps;
    currency: string;
    dataTn?: string;
};

const baseItemFragment = graphql`
    fragment PdpRespSimilarItemCarousel_baseItem on Item {
        isAuctionEnabled
    }
`;

const itemFragment = graphql`
    fragment PdpRespSimilarItemCarousel_item on Item {
        title
        pdpURL
        isArt
        isAuctionEnabled
        creators {
            creator {
                name
            }
        }
        photos(limit: 1) {
            masterOrZoomPath
            versions {
                webPath
            }
        }
        ...FavoritesItem_item
        ...AuctionPricePaddle_item
    }
`;
export const PdpRespSimilarItemCarousel: FC<PdpRespSimilarItemCarouselProps> = ({
    index,
    item: itemRef,
    baseItem: baseItemRef,
    isCondensed,
    onClick = () => {},
    favorites,
    currency,
    dataTn,
}) => {
    const baseItem = useFragment(baseItemFragment, baseItemRef);
    const item = useFragment(itemFragment, itemRef);
    const htmlId = useId();
    const { title, pdpURL, isArt, isAuctionEnabled, creators, photos } = item || {};
    const creatorName = creators?.[0]?.creator?.name;
    const itemPhoto = photos?.[0];
    const imgSrc = itemPhoto?.versions?.[0]?.webPath || '';
    const masterPath = itemPhoto?.masterOrZoomPath;
    const isBaseItemAuctionEnabled = baseItem?.isAuctionEnabled;

    const srcSetAttributes = masterPath
        ? {
              srcSet: getSrcsetString(masterPath),
              sizes: SRC_SET_SIZES_RESP_CAROUSEL_TILE,
          }
        : {};

    const containerClass = classnames(styles.itemContainer, {
        [styles.isCondensed]: !!isCondensed,
    });

    return (
        <div className={containerClass} data-tn={dataTn}>
            {favorites && (
                <FavoritesItem
                    wrapperClass={styles.favoritesWrapper}
                    heartIconWrapperClass={styles.favoritesIconWrapper}
                    folderIconWrapperClass={styles.favoritesIconWrapper}
                    {...favorites}
                    item={item}
                    theme="circle-frosted-medium"
                />
            )}
            <Link
                href={pdpURL || ''}
                onClick={event => onClick(index, event)}
                id={htmlId}
                underline="none"
                className={styles.overlayLink}
            >
                <div className={styles.overlayLinkSqueeze}>
                    {isArt && creatorName && `${creatorName} - `}
                    {title}
                </div>
            </Link>
            {imgSrc && (
                <div className={styles.imageWrapper}>
                    <img
                        className={styles.image}
                        aria-labelledby={htmlId}
                        src={imgSrc}
                        alt={title || ''}
                        {...srcSetAttributes}
                    />
                </div>
            )}

            {isBaseItemAuctionEnabled && item && (
                <AuctionPricePaddle item={item} currency={currency} showPill />
            )}

            {!isBaseItemAuctionEnabled && isAuctionEnabled && (
                <div className={styles.badgeWrapper}>
                    <Badge type="pill" size="small-regular" backgroundColor="moonstone">
                        <FormattedMessage
                            id="PdpRespSimilarItemCarousel.pillText"
                            defaultMessage="Auction"
                        />
                    </Badge>
                </div>
            )}
        </div>
    );
};
